import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CloverOrderService} from '../../../_services/cloverOrder.service';
import {User} from '../../../_models';
import {AESEncryptDecryptService} from '../../../_services/aesencrypt-decrypt.service';
import {CartService} from '../../../_services/cart.service';
import 'rxjs-compat/add/observable/timer';
import {Observable} from 'rxjs';
import {Customer} from '../../../_models/customer';
import {CustomerRewardsService} from '../../../_services/customerRewards.service';
import {Router} from '@angular/router';
import { Constants } from 'src/app/constants/constants';
import { SquarePayServiceService } from 'src/app/_services/square-pay-service.service';
import {UtilsService} from '../../../_services/utils.service';

@Component({
  selector: 'app-notification-popup',
  templateUrl: './notification-payment-required-qr-popup.html',
  styleUrls: ['./notification-payment-required-qr-popup.scss']
})
export class NotificationPaymentRequiredQrPopupComponent implements OnInit {
  readonly Constants = Constants;
  description: string;
  message: string;
  qrUrl: string;
  orderId: string;
  currentUser: User;
  payLinkId: string;
  buttonDisabled = false;
  selectedRole: string;

  // localStorage key holding the scan-to-pay orders that were left OPEN by
  // "Start Next Order" so the customer could finish paying on their phone.
  // Each entry is reconciled on the NEXT reset: if it got paid it is kept (it
  // will be made), if it is still unpaid it is deleted so an abandoned order is
  // never sent to the kitchen. Persisted (not in-memory) so it survives the
  // navigate to /kiosk and any page reload between customers.
  private readonly PENDING_SCAN_TO_PAY_KEY = 'pendingScanToPayOrders';

  constructor(private dialogRef: MatDialogRef<NotificationPaymentRequiredQrPopupComponent>, private cloverOrderService: CloverOrderService,
              private _AESEncryptDecryptService: AESEncryptDecryptService, private rewardsService: CustomerRewardsService,
              private squarePayService: SquarePayServiceService, private cartService: CartService, private utilsService: UtilsService,
              private router: Router, @Inject(MAT_DIALOG_DATA) data) {
    this.description = data.title;
    this.message = data.message;
    this.qrUrl = data.qrUrl;
    this.orderId = data.orderId;
    this.payLinkId = data.payLinkId;
    this.currentUser = utilsService.getCurrentUser();
    this.selectedRole = this.currentUser?.selectedRole;

  }

  ngOnInit() {
  }

  closeAndCancelOrder() {
    this.buttonDisabled = true;
    this.cloverOrderService.deleteOrder(this.orderId).subscribe(() => {
      this.dialogRef.close();
    });
  }

  startOver() {
    this.buttonDisabled = true;
    this.rewardsService.customer = new Observable<Customer>();
    localStorage.setItem('CartItem', this._AESEncryptDecryptService.encrypt(JSON.stringify([])));
    this.cartService.removeAllFromCart();
    this.cartService.removeOrderHistory();
    this.cartService.tip = 0;
    this.cloverOrderService.deleteOrder(this.orderId).subscribe(response => {
      console.log('response ' + response);
      this.dialogRef.close();
      this.router.navigate(['/kiosk']);

    }, error => {
      console.error(error);
      this.dialogRef.close();
      this.router.navigate(['/kiosk']);
    });

  }

  closeAndCancelOrderSquare() {
    if (this.currentUser.role.includes(Constants.TABLE) && this.router.url.includes('/orderHistory')) {
      this.dialogRef.close();
    } else if (this.currentUser.role.includes(Constants.KIOSK) || (this.currentUser.role.includes(Constants.TABLE) &&
        !this.router.url.includes('/orderHistory') )) {
      this.buttonDisabled = true;
      this.squarePayService.deletePaymentLink(this.payLinkId).subscribe(response => {
        console.log('Deleted Payment Link ' + response);
        this.dialogRef.close();
      }, error => {
        console.log(error);
        this.buttonDisabled = false;
      });
    } else if (this.currentUser.role.includes('Admin') || this.currentUser.role.includes(Constants.MANAGER)) {
      this.dialogRef.close();
    }
  }

  close() {
      this.dialogRef.close();
  }

  /**
   * Kiosk scan-to-pay only. Frees the shared kiosk for the next customer
   * WITHOUT cancelling the current order: the current order is left OPEN so the
   * customer can finish paying on their phone from the QR they already scanned.
   *
   * The paid/unpaid check the requirement calls for is deferred, not done at
   * press time — at press time the customer has only just been shown the QR and
   * has not paid yet, so checking "is it paid?" now would cancel every order.
   * Instead we stash the current orderId and, on the NEXT press (i.e. the next
   * customer), reconcile the previously-stashed orders: an order that has since
   * been paid is kept (it will be made); an order that is still unpaid is
   * abandoned, so we delete it and it is never sent to the kitchen. Clover
   * refuses to delete an order that actually carries a payment, so even on a
   * race this can never cancel a paid order.
   */
  startNextOrder(): Promise<void> {
    this.buttonDisabled = true;
    return this.reconcilePendingScanToPayOrders().then(() => {
      // Keep THIS order open for phone payment; hand it to the reconciler for
      // the next reset. Do NOT delete it here.
      this.stashPendingOrder(this.orderId);
      // Reset local kiosk cart state for the next customer (mirrors startOver,
      // minus the deleteOrder — the order must stay open).
      this.rewardsService.customer = new Observable<Customer>();
      localStorage.setItem('CartItem', this._AESEncryptDecryptService.encrypt(JSON.stringify([])));
      this.cartService.removeAllFromCart();
      this.cartService.removeOrderHistory();
      this.cartService.tip = 0;
      this.dialogRef.close();
      this.router.navigate(['/kiosk']);
    });
  }

  private getPendingOrders(): string[] {
    try {
      const raw = localStorage.getItem(this.PENDING_SCAN_TO_PAY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((id) => !!id) : [];
    } catch (e) {
      return [];
    }
  }

  private setPendingOrders(ids: string[]) {
    localStorage.setItem(this.PENDING_SCAN_TO_PAY_KEY, JSON.stringify(ids));
  }

  private stashPendingOrder(id: string) {
    if (!id) {
      return;
    }
    const ids = this.getPendingOrders();
    if (!ids.includes(id)) {
      ids.push(id);
      this.setPendingOrders(ids);
    }
  }

  private orderHasPayment(order: any): boolean {
    if (!order) {
      return false;
    }
    const state = (order.paymentState || '').toString().toUpperCase();
    // PAID / partially paid / refunded all mean money changed hands — keep the
    // order. Only a truly zero-payment order is treated as abandoned.
    if (state === 'PAID' || state === 'REFUNDED' || state === 'PARTIALLY_PAID') {
      return true;
    }
    const payments = order.payments && order.payments.elements ? order.payments.elements : [];
    return Array.isArray(payments) && payments.length > 0;
  }

  /**
   * Reconcile every previously-stashed scan-to-pay order (excluding the one
   * currently on screen). Paid orders are dropped from the pending list and
   * left to be made; unpaid orders are deleted so they are never sent to the
   * kitchen. Anything we cannot conclusively resolve (network error, or Clover
   * refuses the delete because a payment exists) is kept pending and retried on
   * the next reset — we never delete blindly.
   */
  private reconcilePendingScanToPayOrders(): Promise<void> {
    const ids = this.getPendingOrders();
    if (!ids.length) {
      return Promise.resolve();
    }
    // Never touch the order the customer is being asked to pay for right now.
    const remaining: string[] = ids.filter((id) => id === this.orderId);
    const toCheck = ids.filter((id) => id && id !== this.orderId);

    return Promise.all(
      toCheck.map((id) =>
        this.cloverOrderService
          .getOrderWithPayments(id)
          .toPromise()
          .then((order: any) => {
            if (this.orderHasPayment(order)) {
              // Paid -> keep it (it will be made); drop from pending.
              return;
            }
            // Unpaid/abandoned -> delete so it is not made.
            return this.cloverOrderService
              .deleteOrder(id)
              .toPromise()
              .then(() => undefined)
              .catch((err) => {
                // Clover refuses to delete an order with payments; if that
                // happens it WAS paid after all, so keep it pending/safe.
                console.error('startNextOrder: deleteOrder refused for ' + id, err);
                remaining.push(id);
              });
          })
          .catch((err) => {
            // Could not resolve the order — do not risk cancelling a possibly
            // paid order. Keep it pending and retry next reset.
            console.error('startNextOrder: getOrderWithPayments failed for ' + id, err);
            remaining.push(id);
          })
      )
    ).then(() => {
      this.setPendingOrders(remaining);
    });
  }
}
